import type { ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { AiSurface } from '@/components/ui/ai-surface';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import type { ProposedFinding } from '@/lib/api/interviews';

/**
 * One AI-proposed finding from an interview note (moved here from the E07
 * interview card, T-0244): the proposal's summary and confidence on the
 * AI surface (CLAUDE.md hard rule #3), its citations, and the action the
 * Interview notes tab gives it (Confirm, or the state it is in).
 */
export function ProposedFindingCard({
  finding,
  onOpenCite,
  action,
}: {
  finding: ProposedFinding;
  onOpenCite?: (cite: CitationChipData) => void;
  action: ReactNode;
}) {
  const summary = extractFindingSummary(finding.proposed_value);
  return (
    <li>
      <AiSurface
        as="div"
        className="space-y-2 rounded-card border-y-0 border-l-2 border-r-0 p-3"
        role="article"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-meta font-semibold uppercase tracking-wide text-ai-ink">
              <Sparkles className="h-3 w-3" aria-hidden="true" /> Proposed finding
            </div>
            {summary ? (
              <p className="mt-1 text-body-sm leading-snug text-ink-900">{summary}</p>
            ) : (
              <p className="mt-1 text-body-sm italic text-ink-500">No summary text in proposal.</p>
            )}
          </div>
          <AiConfidenceChip value={finding.confidence} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {finding.citations.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5">
              {finding.citations.map((c) => {
                const cite: CitationChipData = {
                  id: c.id,
                  doc_label: c.document_title,
                  section: c.section_path,
                  page: c.page_number,
                  confidence: finding.confidence,
                  status: 'ok',
                };
                return (
                  <li key={c.id}>
                    <CitationChip cite={cite} onOpen={() => onOpenCite?.(cite)} />
                  </li>
                );
              })}
            </ul>
          ) : (
            <span />
          )}
          {action}
        </div>
      </AiSurface>
    </li>
  );
}

/**
 * The human-readable summary in a proposal's `proposed_value`. The shape
 * isn't fixed; common keys are `title`, `summary`, `description`, `finding`.
 */
export function extractFindingSummary(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  for (const key of ['title', 'summary', 'description', 'finding', 'text']) {
    const candidate = v[key];
    if (typeof candidate === 'string' && candidate.length > 0) return candidate;
  }
  return null;
}
